import { useState, useCallback, useEffect, useRef } from "react";
import { Box, Text, TextField, IconButton, Spinner } from "@radix-ui/themes";
import { Cross2Icon } from "@radix-ui/react-icons";

type AutocompleteBaseProps<O> = {
  value: number | null | undefined;
  onChange: (value: number | null | undefined) => void;
  label: string;
  labelWeight?: "medium" | "bold";
  placeholder?: string;
  readOnly?: boolean;
  searchFn: (searchText: string, limit?: number) => Promise<O[]>;
  fetchByIdFn: (id: number) => Promise<O | null>;
  getOptionLabel: (option: O) => string;
  getOptionValue: (option: O) => number;
};

export function AutocompleteBase<O>({
  value,
  onChange,
  label,
  labelWeight = "bold",
  placeholder = "Search...",
  readOnly = false,
  searchFn,
  fetchByIdFn,
  getOptionLabel,
  getOptionValue,
}: AutocompleteBaseProps<O>) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<O[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<O | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justClearedRef = useRef(false);

  // Load the selected option when value changes
  useEffect(() => {
    let cancelled = false;

    async function loadSelectedOption() {
      if (value === null || value === undefined) {
        setSelectedOption(null);
        setInputValue("");
        return;
      }

      if (selectedOption && getOptionValue(selectedOption) === value) {
        return;
      }

      try {
        const option = await fetchByIdFn(value);
        if (!cancelled && option) {
          setSelectedOption(option);
          setInputValue(getOptionLabel(option));
        }
      } catch (err) {
        console.error("Failed to fetch option by ID:", err);
      }
    }

    loadSelectedOption();

    return () => {
      cancelled = true;
    };
  }, [value, selectedOption, fetchByIdFn, getOptionLabel, getOptionValue]);

  const searchWithDebounce = useCallback(
    (searchText: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!searchText.trim()) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      debounceRef.current = setTimeout(async () => {
        try {
          const results = await searchFn(searchText);
          setOptions(results);
        } catch (err) {
          console.error("Failed to search:", err);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [searchFn],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (newValue: string) => {
    if (readOnly) return;
    setInputValue(newValue);
    setOpen(true);
    searchWithDebounce(newValue);

    if (selectedOption && newValue !== getOptionLabel(selectedOption)) {
      setSelectedOption(null);
      onChange(null);
    }
  };

  const handleSelect = (option: O) => {
    if (readOnly) return;
    setSelectedOption(option);
    setInputValue(getOptionLabel(option));
    onChange(getOptionValue(option));
    setOpen(false);
    setOptions([]);
  };

  const handleClear = () => {
    if (readOnly) return;
    justClearedRef.current = true;
    setSelectedOption(null);
    setInputValue("");
    onChange(null);
    setOptions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (readOnly) return;
    if (justClearedRef.current) {
      justClearedRef.current = false;
      return;
    }
    if (inputValue.trim()) {
      setOpen(true);
      searchWithDebounce(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Box ref={containerRef} style={{ position: "relative" }}>
      <Text as="div" size="2" mb="1" weight={labelWeight}>
        {label}
      </Text>
      <Box style={{ position: "relative" }}>
        <TextField.Root
          ref={inputRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
        >
          <TextField.Slot side="right">
            {loading ? (
              <Spinner size="1" />
            ) : value !== null && value !== undefined && !readOnly ? (
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={handleClear}
                style={{ cursor: "pointer" }}
              >
                <Cross2Icon />
              </IconButton>
            ) : null}
          </TextField.Slot>
        </TextField.Root>
      </Box>

      {open && (options.length > 0 || (inputValue.trim() && !loading)) && (
        <Box
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            backgroundColor: "var(--color-panel-solid)",
            border: "1px solid var(--gray-6)",
            borderRadius: "var(--radius-2)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            boxShadow: "var(--shadow-3)",
          }}
        >
          {options.length > 0 ? (
            options.map((option) => (
              <Box
                key={getOptionValue(option)}
                onClick={() => handleSelect(option)}
                style={{
                  padding: "4px 12px",
                  cursor: "pointer",
                  backgroundColor:
                    selectedOption &&
                    getOptionValue(selectedOption) === getOptionValue(option)
                      ? "var(--accent-4)"
                      : "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--gray-4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    selectedOption &&
                    getOptionValue(selectedOption) === getOptionValue(option)
                      ? "var(--accent-4)"
                      : "transparent";
                }}
              >
                <Text size="2">{getOptionLabel(option)}</Text>
              </Box>
            ))
          ) : (
            <Box style={{ padding: "4px 12px" }}>
              <Text size="2" color="gray">
                No results found
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
